import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { useCheckoutStore } from '@jwp/ott-common/src/stores/CheckoutStore';
import { useWatchHistoryStore } from '@jwp/ott-common/src/stores/WatchHistoryStore';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import useEntitlement from '@jwp/ott-hooks-react/src/useEntitlement';
import Play from '@jwp/ott-theme/assets/icons/play.svg?react';
import Clock from '@jwp/ott-theme/assets/icons/clock.svg?react';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { ACCESS_MODEL } from '@jwp/ott-common/src/constants';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { differenceInMilliseconds } from 'date-fns';
import { useTheme } from '../../../../../ThemeContext';
import { Auth } from 'aws-amplify';
import ReactGA from 'react-ga4';
import { useGADeviceType } from '@jwp/ott-common/src/utils/GADeviceType';

import Button from '../../components/Button/Button';
import Icon from '../../components/Icon/Icon';

import styles from './StartWatchingButton.module.scss';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';

type Props = {
  item: PlaylistItem;
  playUrl?: string;
  disabled?: boolean;
  seriesLabel?: React.ReactNode;
  isSeries?: boolean;
  onClick?: () => void;
  isLive?: boolean;
  episodeMetaData?: string;
};

const StartWatchingButton: React.VFC<Props> = ({ item, playUrl, disabled = false, onClick, isLive, episodeMetaData }) => {
  const { t } = useTranslation('video');
  const navigate = useNavigate();
  const location = useLocation();
  const breakpoint = useBreakpoint();
  const { universalStyles } = useTheme();
  const { isContinueWatchingEnabled, isCognitoEnabled, guestStreamingEnabled, isGuestModeEnabled } = useAppConfig();
  const { isAnalyticsEnable } = useAppConfig();
  // account
  const accessModel = useConfigStore((state) => state.accessModel);
  const user = useAccountStore((state) => state.user);
  const isLoggedIn = !!user;
  const userId = isLoggedIn ? user.id : 'Guest user';
  const deviceType = true ? useGADeviceType() : null;

  // watch history
  const watchHistoryItem = useWatchHistoryStore((state) => item && state.getItem(item));
  const videoProgress = watchHistoryItem?.progress;

  // entitlement
  const setRequestedMediaOffers = useCheckoutStore((s) => s.setRequestedMediaOffers);
  const { isEntitled, mediaOffers } = useEntitlement(item);
  const hasMediaOffers = !!mediaOffers.length;
  const [liveEventLabel, setLiveEventLabel] = useState<string>('');
  const [isLiveEventStarted, setIsLiveEventStarted] = useState<boolean>(false);
  const isliveEvent = item?.contentType ? item.contentType === 'LiveEvent' || item.contentType === 'liveEvent' : false;

  const calculateRemainingTime = useCallback(() => {
    const currentTime = new Date();
    if (!item.scheduledStart) {
      return;
    }
    const preLiveStatus = item['VCH.EventState'] === 'PRE_LIVE' || item['VCH.EventState'] === 'LIVE_UNPUBLISHED';
    const StartTimeDifference = differenceInMilliseconds(item.scheduledStart, currentTime);
    const endtimeDifference = item.scheduledEnd ? differenceInMilliseconds(item.scheduledEnd, currentTime) : 0;

    if (preLiveStatus && StartTimeDifference > 300000) {
      setLiveEventLabel('Scheduled');
    } else if (preLiveStatus && StartTimeDifference > 0 && StartTimeDifference <= 300000) {
      const remainingTimeInMilliseconds = differenceInMilliseconds(item.scheduledStart, currentTime);
      const minutes = Math.floor(remainingTimeInMilliseconds / 60000) % 60;
      const seconds = Math.floor((remainingTimeInMilliseconds % 60000) / 1000);
      setLiveEventLabel(`Stream start in : ${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
    } else if (item['VCH.EventState'] === 'INSTANT_VOD' || item['VCH.EventState'] === 'VOD_PUBLIC' || endtimeDifference <= 0) {
      setLiveEventLabel('Start watching');
      setIsLiveEventStarted(true);
    } else if (item['VCH.EventState'] === 'LIVE_PUBLISHED' || (StartTimeDifference < 0 && endtimeDifference > 0)) {
      setLiveEventLabel('Watch live');
      setIsLiveEventStarted(true);
    }
  }, [item]);

  useEffect(() => {
    if (isliveEvent && item.scheduledStart) {
      calculateRemainingTime();
      const interval = setInterval(() => calculateRemainingTime(), 1000);
      return () => clearInterval(interval);
    }
  }, [item?.scheduledStart, item?.contentType, calculateRemainingTime, isliveEvent]);

  const startWatchingLabel = useMemo((): string => {
    if (isEntitled) {
      if (isliveEvent && videoProgress) {
        return t('continue_watching');
      } else if (isliveEvent) {
        return liveEventLabel;
      }
      return typeof videoProgress === 'number'
        ? episodeMetaData
          ? t('continue_watching') + episodeMetaData
          : t('continue_watching')
        : isLive
        ? t('Watch Live')
        : episodeMetaData
        ? t('start_watching') + episodeMetaData
        : t('start_watching');
    }
    if (hasMediaOffers && !isLoggedIn) {
      return t('Log in or subscribe');
    } else if (hasMediaOffers) {
      return t('upgrade');
    }
    if (!isLoggedIn) return t('sign_up_to_start_watching');

    return t('complete_your_subscription');
  }, [isEntitled, isLoggedIn, hasMediaOffers, videoProgress, t, liveEventLabel, isliveEvent, isLive]);

  const handleStartWatchingClick = useCallback(() => {
    if ((!isGuestModeEnabled && !isLoggedIn) || (!guestStreamingEnabled && !isLoggedIn)) {
      if (isAnalyticsEnable) {
        sendGAEvent('signup_prompt_shown', {
          asset_id: item?.mediaOffers?.map((id) => id.offerId).join(','),
          title: item.title,
          device_type: deviceType,
        });
      }
      if (isCognitoEnabled) {
        Auth.federatedSignIn();
      } else {
        navigate(modalURLFromLocation(location, 'create-account'));
        useAccountStore.setState({ mediaOffer: item.mediaOffers });
      }
    } else {
      if (isEntitled) {
        if (isliveEvent && !isLiveEventStarted) {
          return;
        }
        if (onClick) {
          onClick();
          return;
        }
        return playUrl && navigate(playUrl);
      }
      if (!isLoggedIn) {
        if (isAnalyticsEnable) {
          sendGAEvent('signup_prompt_shown', {
            asset_id: item?.mediaOffers?.map((id) => id.offerId).join(','),
            title: item.title,
            device_type: deviceType,
          });
        }
        useAccountStore.setState({ mediaOffer: item.mediaOffers });
        return navigate(modalURLFromLocation(location, 'login'));
      }

      if (hasMediaOffers) return navigate(modalURLFromLocation(location, 'choose-offer')), useAccountStore.setState({ mediaOffer: item.mediaOffers });

      return navigate('/u/payments');
    }
  }, [isEntitled, playUrl, navigate, isLoggedIn, location, hasMediaOffers, onClick, isliveEvent, isLiveEventStarted]);

  useEffect(() => {
    if (startWatchingLabel === 'Watch live') {
      handleStartWatchingClick();
    }
  }, [startWatchingLabel]);

  useEffect(() => {
    // set the TVOD mediaOffers in the checkout store
    setRequestedMediaOffers(mediaOffers);

    return () => setRequestedMediaOffers([]);
  }, [mediaOffers, setRequestedMediaOffers]);

  // the user can't purchase access in an AVOD platform due to missing configuration, so we hide the button
  if (accessModel === ACCESS_MODEL.AVOD && !isEntitled) {
    return null;
  }

  return (
    <Button
      color={isliveEvent && !isLiveEventStarted && isEntitled ? 'liveEventScheduled' : 'primary'}
      variant="startWatchingButton"
      size="exlarge"
      label={startWatchingLabel}
      startIcon={isEntitled ? isliveEvent && !isLiveEventStarted ? <Icon icon={Clock} /> : <Icon icon={Play} /> : undefined}
      onClick={handleStartWatchingClick}
      fullWidth={breakpoint < Breakpoint.md}
      // disabled={hasMediaOffers ? false : isliveEvent ? !isLiveEventStarted : disabled}
      disabled={false}
      textStyle={universalStyles.primaryThemeButtonText}
    >
      {isEntitled && videoProgress && isContinueWatchingEnabled ? (
        <div className={styles.progressRail} style={universalStyles.startWatchingButtonUnProgress}>
          <div className={styles.progress} style={{ width: `${videoProgress * 100}%`, ...universalStyles.startWatchingProgress }} />
        </div>
      ) : null}
    </Button>
  );
};

export default StartWatchingButton;
